import React from "react";
import { apiUrl } from "../../utils/api";
import { fehlerText } from "./ticketFehler";
import { markdownSicherRendern } from "./markdownSicher";
import { Vorschlag, VorschlagKurz, VorschlagListe, vorschlagService } from "./vorschlagService";

/**
 * Tickets Teil 2 — „Lösungsvorschlag" im Ticket-Detail (nur admin/gfgl; die
 * Sichtbarkeit prüft `TicketDetail.tsx` bereits, bevor diese Komponente
 * überhaupt gerendert wird).
 * Spec: docs/superpowers/specs/2026-09-24-tickets-teil2-claude-vorschlag-design.md
 */

const POLL_MS = 30_000;

const datumKurz = (iso: string): string =>
  new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** „v1 · Ticket vom 24.09., 09:00“ bzw. „v2 · nach Ergänzung vom 25.09., 14:02“. */
function versionLabel(v: VorschlagKurz): string {
  return v.stand === 1 ? `v1 · Ticket vom ${datumKurz(v.angelegt)}` : `v${v.stand} · nach Ergänzung vom ${datumKurz(v.angelegt)}`;
}

/** Minuten seit `iso`. Für wartet/gesendet: ab `gesendet ?? angelegt` — nach „Neu erstellen“ einer alten Version wäre `angelegt` Tage alt. */
function minutenSeit(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
}

type Props = { ticketId: number };

export function LoesungsVorschlag({ ticketId }: Props) {
  const [liste, setListe] = React.useState<VorschlagListe | null>(null);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [standAuswahl, setStandAuswahl] = React.useState<number | null>(null);
  const [detail, setDetail] = React.useState<Vorschlag | null>(null);
  const [reiter, setReiter] = React.useState<"analyse" | "plan">("analyse");
  const [aktionLaeuft, setAktionLaeuft] = React.useState(false);

  const laden = React.useCallback(async () => {
    try {
      const l = await vorschlagService.liste(ticketId);
      setListe(l);
      setFehler(null);
      // Beim ersten Laden (und nach „Neu erstellen“) auf die neueste Version
      // springen — eine bestehende Auswahl der Nutzerin bleibt sonst erhalten.
      setStandAuswahl((alt) => (alt != null && l.versionen.some((v) => v.stand === alt) ? alt : (l.neueste?.stand ?? null)));
    } catch (e) {
      setFehler(fehlerText(e, "Lösungsvorschlag konnte nicht geladen werden."));
    }
  }, [ticketId]);

  React.useEffect(() => { void laden(); }, [laden]);

  // Alle 30 s neu laden, solange die NEUESTE Version noch läuft — beim
  // Unmount (Ticket geschlossen) sofort beenden, sonst pollt die Detailseite
  // nach dem Verlassen im Hintergrund weiter.
  React.useEffect(() => {
    const status = liste?.neueste?.status;
    if (status !== "wartet" && status !== "gesendet") return;
    const timer = setInterval(() => { void laden(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [liste?.neueste?.status, laden]);

  // Die Listenzeile trägt keine großen Texte (VorschlagKurz) — für eine
  // ältere, bereits fertige Version werden Analyse/Plan gezielt nachgeladen.
  // Nur „fertig“-Versionen tauchen unten überhaupt in der Auswahl auf.
  React.useEffect(() => {
    if (!liste || standAuswahl == null) { setDetail(null); return; }
    if (liste.neueste && liste.neueste.stand === standAuswahl) { setDetail(liste.neueste); return; }
    let lebt = true;
    vorschlagService.version(ticketId, standAuswahl)
      .then((v) => { if (lebt) setDetail(v); })
      .catch((e) => { if (lebt) setFehler(fehlerText(e, "Version konnte nicht geladen werden.")); });
    return () => { lebt = false; };
  }, [liste, standAuswahl, ticketId]);

  const neuErstellen = async () => {
    setAktionLaeuft(true);
    try { await vorschlagService.neu(ticketId); await laden(); }
    catch (e) { setFehler(fehlerText(e, "Konnte nicht neu erstellt werden.")); }
    finally { setAktionLaeuft(false); }
  };

  const herunterladen = async (datei: "analyse.md" | "plan.md") => {
    if (standAuswahl == null) return;
    try {
      const res = await fetch(apiUrl(vorschlagService.dateiPfad(ticketId, standAuswahl, datei)));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `T-${ticketId}-v${standAuswahl}-${datei}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setFehler(fehlerText(e, "Download fehlgeschlagen. Bitte erneut versuchen."));
    }
  };

  if (!liste) return fehler ? <p className="tk-fehler" role="alert">{fehler}</p> : <p className="tk-leise">Lädt …</p>;
  if (!liste.aktiv) return <p className="tk-leise">Lösungsvorschläge sind in dieser Umgebung aus.</p>;

  const neueste = liste.neueste;

  if (!neueste) {
    return (
      <div>
        {fehler && <p className="tk-fehler" role="alert">{fehler}</p>}
        <button type="button" className="tp-detail-btn" onClick={() => void neuErstellen()} disabled={aktionLaeuft}>
          Vorschlag erstellen
        </button>
      </div>
    );
  }

  if (neueste.status === "wartet" || neueste.status === "gesendet") {
    return (
      <div>
        {fehler && <p className="tk-fehler" role="alert">{fehler}</p>}
        <p className="tk-leise">Claude analysiert … (seit {minutenSeit(neueste.gesendet ?? neueste.angelegt)} Min.)</p>
      </div>
    );
  }

  if (neueste.status === "fehler") {
    return (
      <div>
        <p className="tk-fehler" role="alert">{neueste.fehler || "Der Vorschlag konnte nicht erstellt werden."}</p>
        <button type="button" className="tp-detail-btn" onClick={() => void neuErstellen()} disabled={aktionLaeuft}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  // status === "fertig" — nur fertige Versionen lassen sich sinnvoll anzeigen.
  const fertigeVersionen = liste.versionen.filter((v) => v.status === "fertig");

  return (
    <div className="tk-vorschlag-inhalt">
      {fehler && <p className="tk-fehler" role="alert">{fehler}</p>}
      {fertigeVersionen.length > 1 && (
        <select
          className="tk-select tk-vorschlag-version"
          aria-label="Version"
          value={standAuswahl ?? neueste.stand}
          onChange={(e) => setStandAuswahl(Number(e.target.value))}
        >
          {fertigeVersionen.map((v) => <option key={v.stand} value={v.stand}>{versionLabel(v)}</option>)}
        </select>
      )}
      <div className="rd-seg" role="group" aria-label="Ansicht">
        <button type="button" className={reiter === "analyse" ? "active" : ""} onClick={() => setReiter("analyse")}>Analyse</button>
        <button type="button" className={reiter === "plan" ? "active" : ""} onClick={() => setReiter("plan")}>Plan</button>
      </div>
      {detail ? (
        <div
          className="tk-vorschlag-text"
          dangerouslySetInnerHTML={{ __html: markdownSicherRendern((reiter === "analyse" ? detail.analyse : detail.plan) ?? "") }}
        />
      ) : (
        <p className="tk-leise">Lädt …</p>
      )}
      <div className="tk-anhang-knoepfe">
        <button type="button" className="tp-detail-btn" onClick={() => void herunterladen("analyse.md")}>Analyse als .md</button>
        <button type="button" className="tp-detail-btn" onClick={() => void herunterladen("plan.md")}>Plan als .md</button>
        <span className="tk-flex" />
        <button type="button" className="tp-detail-btn" onClick={() => void neuErstellen()} disabled={aktionLaeuft}>
          Neu erstellen
        </button>
      </div>
    </div>
  );
}
